/**
 * Groomer-facing report editor for a single grooming-appointment booking. Loads any
 * existing report, lets the groomer edit a DRAFT (times, services performed, coat
 * rating, notes, before/after photos), Save draft (create/update) or Publish (locks
 * it, owner-visible). Read-only once PUBLISHED. Esc / backdrop closes.
 */
import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ImageGalleryEditor } from '@/components/image-gallery-editor';
import { form_error_message } from '@/lib/forms';
import { use_groom_report, use_save_groom_report } from '@/features/groom-report/hooks';
import type { Booking } from '@/features/bookings/types';

const field_class =
  'flex h-11 w-full rounded-input border-2 border-input bg-card px-3 py-2 text-sm text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast focus-visible:outline-none focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15 disabled:opacity-60';

/** ISO → the value a <input type="datetime-local"> expects (local wall-clock). */
function iso_to_local(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function GroomReportModal({ booking, on_close }: { booking: Booking; on_close: () => void }) {
  const report_q = use_groom_report(booking.id);
  const { save_m, publish_m } = use_save_groom_report(booking.id);

  const [actual_start, set_actual_start] = useState('');
  const [actual_end, set_actual_end] = useState('');
  const [services_performed, set_services_performed] = useState('');
  const [coat_rating, set_coat_rating] = useState('');
  const [notes, set_notes] = useState('');
  const [error, set_error] = useState<string | null>(null);

  const report = report_q.data ?? null;
  const exists = report !== null;
  const published = report?.status === 'PUBLISHED';
  const read_only = published;

  useEffect(() => {
    if (report_q.isLoading) return;
    set_actual_start(iso_to_local(report?.actual_start ?? null));
    set_actual_end(iso_to_local(report?.actual_end ?? null));
    set_services_performed(report?.services_performed ?? '');
    set_coat_rating(report?.coat_rating != null ? String(report.coat_rating) : '');
    set_notes(report?.notes ?? '');
  }, [report, report_q.isLoading]);

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => e.key === 'Escape' && on_close();
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  function build_payload() {
    return {
      actual_start: actual_start ? new Date(actual_start).toISOString() : undefined,
      actual_end: actual_end ? new Date(actual_end).toISOString() : undefined,
      services_performed: services_performed.trim() || undefined,
      coat_rating: coat_rating.trim() ? Number(coat_rating) : undefined,
      notes: notes.trim() || undefined,
    };
  }

  async function save_draft() {
    set_error(null);
    try {
      await save_m.mutateAsync(build_payload());
    } catch (err) {
      set_error(form_error_message(err, 'Could not save the report.'));
    }
  }

  async function save_and_publish() {
    set_error(null);
    try {
      await save_m.mutateAsync(build_payload());
      await publish_m.mutateAsync();
      on_close();
    } catch (err) {
      set_error(form_error_message(err, 'Could not publish the report.'));
    }
  }

  const busy = save_m.isPending || publish_m.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="groom-report-modal"
      onClick={on_close}
    >
      <div className="my-8 w-full max-w-xl rounded-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="font-heading text-lg font-bold">Groom report</h2>
            <p className="text-xs text-muted-foreground">{published ? 'Published — read only' : exists ? 'Draft — editable' : 'New draft'}</p>
          </div>
          <button type="button" onClick={on_close} aria-label="Close" className="rounded-full p-2 hover:bg-muted" data-testid="groom-report-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {report_q.isLoading ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label>Started</Label>
                <input type="datetime-local" className={field_class} value={actual_start} onChange={(e) => set_actual_start(e.target.value)} disabled={read_only} data-testid="gr-start" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Ended</Label>
                <input type="datetime-local" className={field_class} value={actual_end} onChange={(e) => set_actual_end(e.target.value)} disabled={read_only} data-testid="gr-end" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Coat rating (1–5)</Label>
                <Input type="number" step="1" min="1" max="5" value={coat_rating} onChange={(e) => set_coat_rating(e.target.value)} disabled={read_only} data-testid="gr-coat-rating" />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Services performed</Label>
                <Input value={services_performed} onChange={(e) => set_services_performed(e.target.value)} disabled={read_only} data-testid="gr-services" placeholder="Bath, blow-dry, nail trim, sanitary trim" />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <Label>Notes</Label>
                <textarea className={`${field_class} h-20 resize-y`} value={notes} onChange={(e) => set_notes(e.target.value)} disabled={read_only} data-testid="gr-notes" placeholder="How did the session go?" />
              </div>
            </div>

            {/* Before/after photos — available once a draft exists (needs the report id as owner). */}
            {exists && report && !read_only && (
              <div className="grid gap-4 sm:grid-cols-2">
                <ImageGalleryEditor owner_type="GROOMING_REPORT_BEFORE" owner_id={report.id} label="Before photos" />
                <ImageGalleryEditor owner_type="GROOMING_REPORT_AFTER" owner_id={report.id} label="After photos" />
              </div>
            )}
            {published && report && (report.before_photos.length > 0 || report.after_photos.length > 0) && (
              <p className="text-xs text-muted-foreground">
                {report.before_photos.length} before / {report.after_photos.length} after photo(s) shared with the owner.
              </p>
            )}

            {!exists && !read_only && (
              <p className="text-xs text-muted-foreground">Save a draft first to add before/after photos.</p>
            )}

            {error && <p className="text-sm text-destructive" data-testid="groom-report-error">{error}</p>}

            {!read_only && (
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={save_draft} disabled={busy} data-testid="groom-report-save">
                  {save_m.isPending && !publish_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save draft
                </Button>
                <Button variant="gradient" onClick={save_and_publish} disabled={busy} data-testid="groom-report-publish">
                  {publish_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Publish
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
